import { BadgePill, BrandfolderHexColors, MoreInfoTooltip } from '@brandfolder/react';
import React, { FunctionComponent } from 'react';

import { moreInfoLabel } from '@components/standard-messages';
import { ListOption } from '@components/library/dropdown';
import {
  Brandfolder,
  Portal,
  ServerResourceType
} from '@components/bulk_management/user-settings/resource-sidebar/ResourceTypes';
import { getAssetLibraryTitle } from '@helpers/getty-strings';

interface ResourceDropdownLabelProps {
  resourceLabel: string;
  resourceName: string;
}

export const PermissionLevelTooltip: FunctionComponent = () => (
  <MoreInfoTooltip
    iconLabel={moreInfoLabel}
    id="invitation-form__permission-level__more-info"
    tooltip={
      <>
        <span>
          <strong>
            {bfTranslate('Guests')}
          </strong>: {bfTranslate('View and download assets.')}
        </span><br />
        <span>
          <strong>
            {bfTranslate('Collaborators')}
          </strong>: {bfTranslate('Add and edit assets, plus all guest abilities.')}
        </span><br />
        <span>
          <strong>
            {bfTranslate('Admins')}
          </strong>: {bfTranslate('Full access to managing assets, users, and settings.')}
        </span>
      </>
    }
  />
);

const ResourceDropdownLabel: FunctionComponent<ResourceDropdownLabelProps> = ({ resourceLabel, resourceName }) => (
  <span className="resource-dropdown-label">
    {resourceName}
    <BadgePill
      backgroundColor={BrandfolderHexColors.DirtyMarshmallowHalf}
      borderColor={BrandfolderHexColors.DirtyMarshmallowHalf}
      className="resource-dropdown-label__type-badge"
      radius={12}
    >
      {resourceLabel}
    </BadgePill>
  </span>
);

const translatedClosePill = bfTranslate('Close pill');

export const generateBrandfolderCollectionOptions = (
  brandfolders: Brandfolder[],
  portals: Portal[],
  selectedResources: Array<{
    type: ServerResourceType;
    key: string;
  }>
): ListOption[] => {
  const selectedBrandfolderKeys = selectedResources
    .filter((resource) => resource.type === 'brandfolder')
    .map(({ key }) => key);
  const selectedCollectionKeys = selectedResources
    .filter((resource) => resource.type === 'collection')
    .map(({ key }) => key);
  const options: ListOption[] = [];
  const brandguideOptions: ListOption[] = [];
  brandfolders.forEach((brandfolder) => {
    const brandfolderSelected = !!selectedBrandfolderKeys.find((key) => key === brandfolder.key);
    options.push({
      closeButtonAriaLabel: `${translatedClosePill} ${brandfolder.name}`,
      label: (
        <ResourceDropdownLabel
          resourceLabel={getAssetLibraryTitle()}
          resourceName={brandfolder.name}
        />
      ),
      value: `brandfolder|${brandfolder.key}`,
      isDisabled: !brandfolder.adminable
    });

    brandfolder.collections.forEach((collection) => {
      const collectionSelected = !!selectedCollectionKeys.find((key) => key === collection.key);
      options.push({
        closeButtonAriaLabel: `${translatedClosePill} ${collection.name}`,
        label: (
          <ResourceDropdownLabel
            resourceLabel={bfTranslate('Collection')}
            resourceName={collection.name}
          />
        ),
        value: `collection|${collection.key}`,
        isIndented: true,
        isDisabled: brandfolderSelected || !collection.adminable
      });
      collection.subcollections?.forEach((subcollection) => {
        options.push({
          closeButtonAriaLabel: `${translatedClosePill} ${subcollection.name}`,
          label: (
            <ResourceDropdownLabel
              resourceLabel={bfTranslate('Subcollection')}
              resourceName={subcollection.name}
            />
          ),
          value: `collection|${subcollection.key}`,
          isDoubleIndented: true,
          isDisabled: brandfolderSelected || collectionSelected || !subcollection.adminable
        });
      });
    });

    brandfolder.workspaces.forEach((workspace) => {
      options.push({
        closeButtonAriaLabel: `${translatedClosePill} ${workspace.name}`,
        label: (
          <ResourceDropdownLabel
            resourceLabel={bfTranslate('Workspace')}
            resourceName={workspace.name}
          />
        ),
        value: `collection|${workspace.key}`,
        isIndented: true,
        isDisabled: brandfolderSelected || !workspace.adminable,
      });
    });

    brandfolder.brandguides.forEach((brandguide) => {
      brandguideOptions.push({
        closeButtonAriaLabel: `${translatedClosePill} ${brandguide.name}`,
        label: (
          <ResourceDropdownLabel
            resourceLabel={bfTranslate('Brandguide')}
            resourceName={brandguide.name}
          />
        ),
        value: `brandguide|${brandguide.key}`,
        isDisabled: !brandguide.adminable
      });
    });
  });

  const portalOptions: ListOption[] = portals.map((portal) => ({
    closeButtonAriaLabel: `${translatedClosePill} ${portal.name}`,
    label: (
      <ResourceDropdownLabel
        resourceLabel={bfTranslate('Portal')}
        resourceName={portal.name}
      />
    ),
    value: `portal|${portal.key}`,
    isDisabled: !portal.adminable
  }));

  return options.concat(brandguideOptions).concat(portalOptions);
};